<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { auth, db, storage } from "@/firebase";
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import StudentHeader from "@/components/StudentHeader.vue";
import StudentAnalytics from "@/components/StudentAnalytics.vue";
import PostTestHistoryGrid from "@/components/Student/PostTestHistoryGrid.vue";
import { useProfilePhoto } from "@/composables/useProfilePhoto";
import { useToast } from "@/composables/useToast";
import { PencilSquareIcon, UserIcon } from "@heroicons/vue/24/outline";
import EditBioModal from "@/components/Student-Tutor/Profile/EditBioModal.vue";
import EditSubjectsModal from "@/components/Student-Tutor/Profile/EditSubjectsModal.vue";
import EditLearningStylesModal from "@/components/Student-Tutor/Profile/EditLearningStylesModal.vue";
import UploadPhotoModal from "@/components/Student-Tutor/Profile/UploadPhotoModal.vue";
import EditTextFieldModal from "@/components/Student-Tutor/Profile/EditTextFieldModal.vue";
import EditLocationModal from "@/components/Student-Tutor/Profile/EditLocationModal.vue";
import EditGradeTrackModal from "@/components/Student-Tutor/Profile/EditGradeTrackModal.vue";
import IDUpdateReminderModal from "@/components/Student-Tutor/Profile/IDUpdateReminderModal.vue";
import UploadIDModal from "@/components/Student-Tutor/Profile/UploadIDModal.vue";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import DocumentResubmissionAlert from "@/components/Student/DocumentResubmissionAlert.vue";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";
import { logProfileChange } from "@/services/auditLogService";
import FileReport from "@/components/FileReport.vue";

const activeTab = ref("analytics");
const profileImage = ref<string | null>(null);
const userData = ref<any>(null);
const isLoadingProfile = ref(false);
const sessionCount = ref(0);
const averageRating = ref(0);

// Modal states
const isBioModalOpen = ref(false);
const isSubjectsModalOpen = ref(false);
const isPhotoModalOpen = ref(false);
const isLearningStyleModalOpen = ref(false);
const isPhoneModalOpen = ref(false);
const isLocationModalOpen = ref(false);
const isSchoolModalOpen = ref(false);
const isGradeTrackModalOpen = ref(false);
const isIDReminderModalOpen = ref(false);
const isIDUploadModalOpen = ref(false);
const isSaving = ref(false);
const isUploadingID = ref(false);

// Grade change tracking
const previousGradeLevel = ref<string>("");
const previousGradeForReminder = ref<string>("");
const currentGradeForReminder = ref<string>("");
const idUpdateChangeType = ref<"grade" | "school">("grade");

// Available subjects for students
const availableSubjects = [
  "Oral Communication",
  "Reading and Writing",
  "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
  "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
  "21st Century Literature from the Philippines and the World",
  "General Mathematics",
  "Statistics and Probability",
  "Earth and Life Science",
  "Physical Science",
  "Earth Science (for STEM strand)",
];

// Available learning styles
const availableLearningStyles = [
  "Visual Learning",
  "Auditory Learning",
  "Reading/Writing",
  "Kinesthetic Learning",
  "Practice Problems",
  "Group Discussions",
  "One-on-One Tutoring",
  "Self-Study",
  "Interactive Activities",
  "Real-world Examples",
];

// Profile photo composable
const { isUploading, uploadError, uploadSuccess, selectAndUploadPhoto } = useProfilePhoto();

// Toast notifications
const { showSuccess, showError } = useToast();

// Custom notification popup (like ManageAvailability style)
const showNotificationPopup = ref(false);
const notificationMessage = ref("");
const notificationType = ref<"success" | "error">("success");

const showNotification = (message: string, type: "success" | "error" = "success") => {
  notificationMessage.value = message;
  notificationType.value = type;
  showNotificationPopup.value = true;
};

const closeNotificationPopup = () => {
  showNotificationPopup.value = false;
  notificationMessage.value = "";
};

// Computed property to check if profile image exists
const hasProfileImage = computed(() => !!profileImage.value);

// Computed properties for user data
const fullName = computed(() => {
  if (!userData.value) return "Loading...";
  const parts = [
    userData.value.firstName,
    userData.value.middleName,
    userData.value.lastName,
    userData.value.suffix,
  ].filter(Boolean);
  return parts.join(" ") || "Student";
});

const gradeAndTrack = computed(() => {
  if (!userData.value) return "";

  // Handle grade level (e.g., "12th" or "12")
  let grade = "";
  if (userData.value.gradeLevel) {
    const gradeValue = userData.value.gradeLevel.toString();
    // If it already has "th", "st", "nd", "rd" suffix, use as-is
    if (/\d+(st|nd|rd|th)$/i.test(gradeValue)) {
      grade = gradeValue;
    } else {
      // Otherwise, add "Grade" prefix
      grade = `Grade ${gradeValue}`;
    }
  }

  // Capitalize academic track (e.g., "stem" -> "STEM")
  const track = userData.value.academicTrack ? userData.value.academicTrack.toUpperCase() : "";

  return [grade, track].filter(Boolean).join(" - ") || "Not specified";
});

const userEmail = computed(() => userData.value?.email || auth.currentUser?.email || "");
const userPhone = computed(() => userData.value?.phoneNumber || "Not provided");
const userCity = computed(() => userData.value?.city || "Not provided");
const userSchool = computed(() => userData.value?.school || "Not specified");
const userBio = computed(() => userData.value?.bio || "No bio yet.");
const userGradeLevel = computed(() => userData.value?.gradeLevel || "");
const userAcademicTrack = computed(() => userData.value?.academicTrack || "");

// Phone validation function
const validatePhone = (phone: string): string | null => {
  if (!phone) {
    return "Phone number is required";
  }

  // Philippine phone number formats:
  // +639xxxxxxxxx (13 digits)
  // 09xxxxxxxxx (11 digits)
  // 639xxxxxxxxx (12 digits)
  const patterns = [
    /^\+639\d{9}$/, // +639123456789
    /^09\d{9}$/, // 09123456789
    /^639\d{9}$/, // 639123456789
  ];

  const isValid = patterns.some((pattern) => pattern.test(phone));

  if (!isValid) {
    return "Invalid phone number. Use format: +639XXXXXXXXX or 09XXXXXXXXX";
  }

  return null;
};

const userSubjects = computed(() => {
  if (!userData.value?.subjects) return [];
  const subjects = userData.value.subjects;
  if (Array.isArray(subjects)) return subjects;
  if (typeof subjects === "string")
    return subjects
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean);
  return [];
});

const userLearningStyles = computed(() => {
  if (!userData.value?.learningStyles) return [];
  const styles = userData.value.learningStyles;
  if (Array.isArray(styles)) return styles;
  if (typeof styles === "string")
    return styles
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean);
  return [];
});

const memberSince = computed(() => {
  if (!userData.value?.createdAt) return "Recently";
  try {
    const date = userData.value.createdAt.toDate
      ? userData.value.createdAt.toDate()
      : new Date(userData.value.createdAt);
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
  } catch {
    return "Recently";
  }
});

const lastActive = computed(() => {
  if (!userData.value?.lastActive) return "Recently";
  try {
    const date = userData.value.lastActive.toDate
      ? userData.value.lastActive.toDate()
      : new Date(userData.value.lastActive);
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
  } catch {
    return "Recently";
  }
});

// Load session statistics
const loadSessionStats = async (userId: string) => {
  try {
    // Count completed sessions
    const sessionsQuery = query(
      collection(db, "sessions"),
      where("studentId", "==", userId),
      where("sessionStatus", "==", "completed")
    );
    const sessionsSnapshot = await getDocs(sessionsQuery);
    sessionCount.value = sessionsSnapshot.size;

    // ✅ Read pre-calculated average rating from user document (INSTANT!)
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      averageRating.value = userData?.averageRating || 0;

      logInfo(
        `Loaded ${sessionCount.value} completed sessions and pre-calculated rating (${averageRating.value})`
      );
    } else {
      averageRating.value = 0;
    }
  } catch (error) {
    logError("Error loading session stats", error);
  }
};

// Load user profile data
const loadUserProfile = async () => {
  try {
    isLoadingProfile.value = true;

    // Wait for auth to be ready
    const user = await new Promise<any>((resolve) => {
      if (auth.currentUser) {
        resolve(auth.currentUser);
      } else {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
      }
    });

    if (!user) {
      logWarn("No user logged in");
      return;
    }

    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists()) {
      userData.value = userDoc.data();
      profileImage.value = userData.value?.profileImage || null;
      previousGradeLevel.value = userData.value?.gradeLevel || "";
      logInfo("User profile loaded successfully");

      // Load session stats
      await loadSessionStats(user.uid);

      // Check if ID update reminder should be shown
      if (userData.value?.idNeedsUpdate === true) {
        // Check if it's a school change or grade change
        if (userData.value?.previousSchool) {
          idUpdateChangeType.value = "school";
          previousGradeForReminder.value = userData.value?.previousSchool || "";
          currentGradeForReminder.value = userData.value?.school || "";
        } else {
          idUpdateChangeType.value = "grade";
          previousGradeForReminder.value = userData.value?.previousGradeLevel || "";
          currentGradeForReminder.value = userData.value?.gradeLevel || "";
        }
        isIDReminderModalOpen.value = true;
        logDebug("ID update reminder displayed");
      }
    }
  } catch (error) {
    logError("Error loading user profile", error);
  } finally {
    isLoadingProfile.value = false;
  }
};

// Handle bio save
const handleBioSave = async (newBio: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const oldBio = userData.value?.bio || "";

    await updateDoc(userRef, {
      bio: newBio,
      updatedAt: new Date(),
    });

    // Create audit log for bio change
    await logProfileChange(user.uid, "bio", oldBio, newBio, "student");

    if (userData.value) {
      userData.value.bio = newBio;
    }

    logSuccess("Bio updated successfully");
    isBioModalOpen.value = false;
  } catch (error) {
    logError("Error updating bio", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle subjects save
const handleSubjectsSave = async (newSubjects: string[]) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      subjects: newSubjects,
      updatedAt: new Date(),
    });

    if (userData.value) {
      userData.value.subjects = newSubjects;
    }

    logSuccess("Subjects updated successfully");
    isSubjectsModalOpen.value = false;
  } catch (error) {
    logError("Error updating subjects", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle learning styles save
const handleLearningStylesSave = async (newStyles: string[]) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const oldStyles = userData.value?.learningStyles || [];

    await updateDoc(userRef, {
      learningStyles: newStyles,
      updatedAt: new Date(),
    });

    // Create audit log for learning styles change
    await logProfileChange(
      user.uid,
      "learningStyles",
      oldStyles.join(", "),
      newStyles.join(", "),
      "student"
    );

    if (userData.value) {
      userData.value.learningStyles = newStyles;
    }

    logSuccess("Learning styles updated successfully");
    isLearningStyleModalOpen.value = false;
  } catch (error) {
    logError("Error updating learning styles", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle phone save
const handlePhoneSave = async (newPhone: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const oldPhone = userData.value?.phoneNumber || "";

    await updateDoc(userRef, {
      phoneNumber: newPhone,
      updatedAt: new Date(),
    });

    // Create audit log for phone number change
    await logProfileChange(user.uid, "phoneNumber", oldPhone, newPhone, "student");

    if (userData.value) {
      userData.value.phoneNumber = newPhone;
    }

    logSuccess("Phone number updated successfully");
    isPhoneModalOpen.value = false;
  } catch (error) {
    logError("Error updating phone number", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle location save
const handleLocationSave = async (newCity: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const oldCity = userData.value?.city || "";

    await updateDoc(userRef, {
      city: newCity,
      updatedAt: new Date(),
    });

    // Create audit log for location change
    await logProfileChange(user.uid, "city", oldCity, newCity, "student");

    if (userData.value) {
      userData.value.city = newCity;
    }

    logSuccess("Location updated successfully");
    isLocationModalOpen.value = false;
  } catch (error) {
    logError("Error updating location", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle school save
const handleSchoolSave = async (newSchool: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const oldSchool = userData.value?.school || "";

    await updateDoc(userRef, {
      school: newSchool,
      idNeedsUpdate: true,
      previousSchool: oldSchool,
      updatedAt: new Date(),
    });

    // Create audit log for school change
    await logProfileChange(user.uid, "school", oldSchool, newSchool, "student");

    if (userData.value) {
      userData.value.school = newSchool;
      userData.value.idNeedsUpdate = true;
    }

    logSuccess("School updated successfully");
    isSchoolModalOpen.value = false;

    // Show ID update reminder modal after short delay
    setTimeout(() => {
      idUpdateChangeType.value = "school";
      isIDReminderModalOpen.value = true;
      previousGradeForReminder.value = oldSchool;
      currentGradeForReminder.value = newSchool;
    }, 300);
  } catch (error) {
    logError("Error updating school", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle grade and track save
const handleGradeTrackSave = async (newGradeLevel: string, newTrack: string) => {
  try {
    isSaving.value = true;
    const user = auth.currentUser;
    if (!user) return;

    const oldGradeLevel = previousGradeLevel.value;
    const gradeChanged = oldGradeLevel && oldGradeLevel !== newGradeLevel;

    const userRef = doc(db, "users", user.uid);
    const oldTrack = userData.value?.academicTrack || "";

    const updateData: any = {
      gradeLevel: newGradeLevel,
      academicTrack: newTrack,
      updatedAt: new Date(),
    };

    // If grade level changed, set the ID update reminder flag
    if (gradeChanged) {
      updateData.idNeedsUpdate = true;
      updateData.previousGradeLevel = oldGradeLevel;
      previousGradeForReminder.value = oldGradeLevel;
      currentGradeForReminder.value = newGradeLevel;
      logDebug(`Grade changed - ID update required`);
    }

    await updateDoc(userRef, updateData);

    // Create audit logs for grade level and academic track changes
    if (gradeChanged) {
      await logProfileChange(user.uid, "gradeLevel", oldGradeLevel, newGradeLevel, "student");
    }

    if (oldTrack !== newTrack) {
      await logProfileChange(user.uid, "academicTrack", oldTrack, newTrack, "student");
    }

    if (userData.value) {
      userData.value.gradeLevel = newGradeLevel;
      userData.value.academicTrack = newTrack;
      if (gradeChanged) {
        userData.value.idNeedsUpdate = true;
        userData.value.previousGradeLevel = oldGradeLevel;
      }
    }

    // Update the tracking ref
    previousGradeLevel.value = newGradeLevel;

    logSuccess("Grade and track updated successfully");
    isGradeTrackModalOpen.value = false;

    // Show ID update reminder if grade changed
    if (gradeChanged) {
      setTimeout(() => {
        idUpdateChangeType.value = "grade";
        isIDReminderModalOpen.value = true;
      }, 300);
    }
  } catch (error) {
    logError("Error updating grade and track", error);
  } finally {
    isSaving.value = false;
  }
};

// Handle profile photo upload
const handlePhotoUpload = async () => {
  try {
    const result = await selectAndUploadPhoto();

    if (result.success && result.url) {
      profileImage.value = result.url;
      logSuccess("Profile photo updated successfully");

      // Show success notification
      setTimeout(() => {
        uploadSuccess.value = false;
      }, 3000);
    } else if (result.error) {
      logError("Photo upload failed", result.error);
    }
  } catch (error) {
    logError("Error handling photo upload", error);
  }
};

// Handle photo upload from modal
const handlePhotoUploadFromModal = async (file: File) => {
  try {
    const { uploadProfilePhoto } = useProfilePhoto();
    const result = await uploadProfilePhoto(file);

    if (result.success && result.url) {
      profileImage.value = result.url;
      logSuccess("Profile photo updated successfully");
      isPhotoModalOpen.value = false;

      // Show success notification
      setTimeout(() => {
        uploadSuccess.value = false;
      }, 3000);
    } else if (result.error) {
      logError("Photo upload failed", result.error);
    }
  } catch (error) {
    logError("Error handling photo upload", error);
  }
};

// Handle ID reminder modal actions
const handleIDReminderUpload = () => {
  isIDReminderModalOpen.value = false;
  setTimeout(() => {
    isIDUploadModalOpen.value = true;
  }, 300);
};

const handleIDReminderDismiss = () => {
  isIDReminderModalOpen.value = false;
  logDebug("ID update reminder dismissed (will show again on next login)");
};

const handleIDReminderClose = () => {
  isIDReminderModalOpen.value = false;
};

// Handle ID upload (both selfie and ID)
const handleIDUpload = async (selfieFile: File, idFile: File) => {
  try {
    isUploadingID.value = true;
    const user = auth.currentUser;
    if (!user) {
      logError("No user logged in");
      return;
    }

    logDebug("Uploading selfie with ID and school ID...");

    const timestamp = Date.now();
    const uploadedAt = new Date();

    // Upload selfie holding ID
    const selfieFilePath = `student-ids/${user.uid}/student-holding-id-${
      user.uid
    }-${timestamp}.${selfieFile.name.split(".").pop()}`;
    const selfieFileRef = storageRef(storage, selfieFilePath);
    const selfieSnapshot = await uploadBytes(selfieFileRef, selfieFile);
    const selfieDownloadURL = await getDownloadURL(selfieSnapshot.ref);
    logInfo("Selfie holding ID uploaded");

    // Upload school ID
    const idFilePath = `student-ids/${user.uid}/student-id-${user.uid}-${timestamp}.${idFile.name
      .split(".")
      .pop()}`;
    const idFileRef = storageRef(storage, idFilePath);
    const idSnapshot = await uploadBytes(idFileRef, idFile);
    const idDownloadURL = await getDownloadURL(idSnapshot.ref);
    logInfo("School ID uploaded");

    // Prepare documents structure matching registration format
    const documentsData = {
      studentHoldingIdFile: {
        downloadURL: selfieDownloadURL,
        fileName: selfieFile.name,
        fileSize: selfieFile.size,
        fileType: selfieFile.type,
        uploadedAt: uploadedAt,
      },
      studentIDFile: {
        downloadURL: idDownloadURL,
        fileName: idFile.name,
        fileSize: idFile.size,
        fileType: idFile.type,
        uploadedAt: uploadedAt,
      },
    };

    // Update Firestore
    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      documents: documentsData,
      idNeedsUpdate: false,
      idUpdatedAt: uploadedAt,
      updatedAt: uploadedAt,
    });

    if (userData.value) {
      userData.value.documents = documentsData;
      userData.value.idNeedsUpdate = false;
    }

    logSuccess("Both documents uploaded successfully");

    // Log profile change for audit
    await logProfileChange(user.uid, "documents", "N/A", "Updated student ID documents", "student");

    isIDUploadModalOpen.value = false;

    // Show success notification popup
    showNotification(
      "Both documents uploaded successfully! Your documents will be reviewed by administrators.",
      "success"
    );
  } catch (error) {
    logError("Error uploading documents", error);
    showNotification("Failed to upload documents. Please try again.", "error");
  } finally {
    isUploadingID.value = false;
  }
};

// Handle viewing post-test details
const handleViewPostTest = (test: any) => {
  logInfo("Viewing post-test details", {
    testId: test.id.substring(0, 8) + "***",
    topic: test.topic,
  });

  // You can implement a modal or navigate to a detailed view
  // For now, just show an alert with the test info
  alert(
    `Post-Test Details:\n\nTopic: ${test.topic}\nSubject: ${test.subject}\nScore: ${
      test.score
    }%\nStatus: ${test.passed ? "PASSED" : "FAILED"}`
  );
};

onMounted(() => {
  document.title = "ElevatED – Student Profile";
  loadUserProfile();
});
</script>

<template>
  <FileReport />
  <div class="student-page-wrapper">
    <StudentHeader />

    <!-- Main Content -->
    <div
      class="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-20 sm:pt-24 md:pt-32 lg:pt-44 pb-6 sm:pb-8"
    >
      <div
        class="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-6xl mx-auto lg:items-start"
      >
        <!-- Left Column - Profile and Contact -->
        <div class="w-full space-y-4 sm:space-y-4 sticky-sidebar">
          <!-- Profile Card -->
          <div
            class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
          >
            <div class="text-center">
              <!-- Profile Avatar -->
              <div class="relative inline-block mb-3 sm:mb-4">
                <UserAvatar :src="profileImage" :name="fullName" size="2xl" />
                <!-- Loading Overlay -->
                <div
                  v-if="isUploading"
                  class="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="animate-spin h-6 w-6 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <!-- Edit Button -->
                <button
                  @click="isPhotoModalOpen = true"
                  :disabled="isUploading"
                  class="absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 bg-orange-500 rounded-full flex items-center justify-center border-2 border-white hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  title="Upload profile photo cursor-pointer"
                >
                  <svg
                    v-if="!isUploading"
                    class="w-3 h-3 sm:w-4 sm:h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="animate-spin h-3 w-3 sm:h-4 sm:w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </button>
              </div>

              <!-- Name and Info -->
              <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                {{ fullName }}
              </h2>
              <p v-if="gradeAndTrack" class="text-gray-600 mb-1 text-sm sm:text-base">
                {{ gradeAndTrack }}
              </p>
              <p class="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4">
                {{ userSchool }}
              </p>

              <!-- Separator Line -->
              <div class="w-full h-px bg-gray-200 mb-3 sm:mb-4"></div>

              <!-- Stats -->
              <div class="flex justify-center space-x-6 sm:space-x-8">
                <div class="text-center">
                  <div class="text-2xl sm:text-3xl font-bold text-blue-600">{{ sessionCount }}</div>
                  <div class="text-xs sm:text-sm text-gray-600">Sessions</div>
                </div>
                <div class="text-center">
                  <div class="text-2xl sm:text-3xl font-bold text-orange-500">
                    {{ averageRating ? Number(averageRating).toFixed(1) : "-" }}
                  </div>
                  <div class="text-xs sm:text-sm text-gray-600">Rating</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Information Card -->
          <div
            class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
          >
            <div class="flex items-center mb-3 sm:mb-4">
              <UserIcon class="w-5 h-5 text-blue-600 mr-2" />
              <h3 class="text-base sm:text-lg font-semibold text-gray-900">Contact Information</h3>
            </div>

            <div class="space-y-2 sm:space-y-3">
              <div v-if="userEmail" class="flex items-center text-gray-600 min-w-0">
                <svg
                  class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,5.11 21.1,4 20,4Z"
                  />
                </svg>
                <span class="text-xs sm:text-sm truncate" :title="userEmail">{{ userEmail }}</span>
              </div>
              <div class="flex items-center justify-between text-gray-600 gap-2">
                <div class="flex items-center flex-1 min-w-0">
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"
                    />
                  </svg>
                  <span class="text-xs sm:text-sm truncate" :title="userPhone">{{
                    userPhone
                  }}</span>
                </div>
                <button
                  @click="isPhoneModalOpen = true"
                  class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
                  title="Edit phone"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                </button>
              </div>
              <div class="flex items-center justify-between text-gray-600 gap-2">
                <div class="flex items-center flex-1 min-w-0">
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"
                    />
                  </svg>
                  <span class="text-xs sm:text-sm truncate" :title="userCity">{{ userCity }}</span>
                </div>
                <button
                  @click="isLocationModalOpen = true"
                  class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
                  title="Edit location"
                >
                  <PencilSquareIcon class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Analytics, About Me, Academic Info, Learning Preferences -->
        <div class="w-full lg:col-span-2 space-y-4 sm:space-y-6">
          <!-- Tab Navigation -->
          <div class="flex space-x-2 mb-4 sm:mb-6">
            <button
              @click="activeTab = 'analytics'"
              :class="[
                'flex-1 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 border-2',
                activeTab === 'analytics'
                  ? 'bg-blue-600 text-white border-black shadow-[4px_2px_0_0_rgba(0,0,0,1)] cursor-pointer'
                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:text-gray-900 hover:border-gray-400 cursor-pointer',
              ]"
            >
              <div class="flex items-center justify-center">
                <svg
                  class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" />
                </svg>
                Analytics
              </div>
            </button>
            <button
              @click="activeTab = 'profile'"
              :class="[
                'flex-1 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 border-2',
                activeTab === 'profile'
                  ? 'bg-blue-600 text-white border-black shadow-[4px_2px_0_0_rgba(0,0,0,1)] cursor-pointer'
                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:text-gray-900 hover:border-gray-400 cursor-pointer',
              ]"
            >
              <div class="flex items-center justify-center">
                <svg
                  class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
                Profile Details
              </div>
            </button>
          </div>

          <!-- Analytics Tab Content -->
          <div v-if="activeTab === 'analytics'">
            <StudentAnalytics />

            <!-- Post-Test History Grid -->
            <PostTestHistoryGrid @view="handleViewPostTest" />
          </div>

          <!-- Profile Tab Content -->
          <div v-if="activeTab === 'profile'" class="space-y-4 sm:space-y-6">
            <!-- Document Resubmission Alert -->
            <DocumentResubmissionAlert />

            <!-- About Me Card -->
            <div
              class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
            >
              <div class="flex items-center justify-between mb-3 sm:mb-4">
                <div class="flex items-center">
                  <svg
                    class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"
                    />
                  </svg>
                  <h3 class="text-base sm:text-lg font-semibold text-gray-900">About Me</h3>
                </div>
                <button
                  @click="isBioModalOpen = true"
                  class="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                  title="Edit bio"
                >
                  <PencilSquareIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
              <p class="text-gray-600 leading-relaxed text-xs sm:text-sm">
                {{ userBio }}
              </p>
              <div class="mt-3 sm:mt-4 text-xs text-gray-500">
                <span class="block sm:inline">Member since {{ memberSince }}</span>
                <span class="hidden sm:inline"> • </span>
                <span class="block sm:inline">Last active {{ lastActive }}</span>
              </div>
            </div>

            <!-- Academic Information Card -->
            <div
              class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
            >
              <div class="flex items-center mb-3 sm:mb-4">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z"
                  />
                </svg>
                <h3 class="text-base sm:text-lg font-semibold text-gray-900">
                  Academic Information
                </h3>
              </div>

              <div class="space-y-3 sm:space-y-4">
                <div>
                  <div class="flex items-center justify-between mb-1">
                    <h4 class="text-xs font-medium text-gray-500">School</h4>
                    <button
                      @click="isSchoolModalOpen = true"
                      class="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                      title="Edit school"
                    >
                      <PencilSquareIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                  <p class="text-xs sm:text-sm text-gray-900">{{ userSchool }}</p>
                </div>

                <div>
                  <div class="flex items-center justify-between mb-1">
                    <h4 class="text-xs font-medium text-gray-500">Grade Level & Track</h4>
                    <button
                      @click="isGradeTrackModalOpen = true"
                      class="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                      title="Edit grade & track"
                    >
                      <PencilSquareIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                  <p class="text-xs sm:text-sm text-gray-900">{{ gradeAndTrack }}</p>
                </div>

                <div>
                  <div class="flex items-center justify-between mb-2">
                    <h4 class="text-xs font-medium text-gray-500">Subjects of Interest</h4>
                    <button
                      @click="isSubjectsModalOpen = true"
                      class="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                      title="Edit subjects"
                    >
                      <PencilSquareIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                  <div v-if="userSubjects.length > 0" class="flex flex-wrap gap-1.5 sm:gap-2">
                    <span
                      v-for="(subject, index) in userSubjects"
                      :key="index"
                      class="px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {{ subject }}
                    </span>
                  </div>
                  <p v-else class="text-xs sm:text-sm text-gray-500 italic">
                    No subjects specified
                  </p>
                </div>
              </div>
            </div>

            <!-- Learning Preferences Card -->
            <div
              class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
            >
              <div class="flex items-center justify-between mb-3 sm:mb-4">
                <div class="flex items-center">
                  <svg
                    class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"
                    />
                  </svg>
                  <h3 class="text-base sm:text-lg font-semibold text-gray-900">
                    Learning Preferences
                  </h3>
                </div>
                <button
                  @click="isLearningStyleModalOpen = true"
                  class="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                  title="Edit learning style"
                >
                  <PencilSquareIcon class="w-5 h-5" />
                </button>
              </div>

              <div>
                <h4 class="text-xs font-medium text-gray-500 mb-2">Learning Style</h4>
                <div v-if="userLearningStyles.length > 0" class="flex flex-wrap gap-1.5 sm:gap-2">
                  <span
                    v-for="(style, index) in userLearningStyles"
                    :key="index"
                    class="px-2 py-0.5 sm:py-1 bg-orange-100 text-orange-800 text-xs rounded-full"
                  >
                    {{ style }}
                  </span>
                </div>
                <p v-else class="text-xs sm:text-sm text-gray-500 italic">
                  No learning styles specified
                </p>
              </div>
            </div>

            <!-- Google Calendar Integration Card -->
            <div
              class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black p-4 sm:p-6"
            >
              <div class="flex items-center mb-3 sm:mb-4">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 class="text-base sm:text-lg font-semibold text-gray-900">
                  Calendar Integration
                </h3>
              </div>
              <GoogleCalendarConnect />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Notification -->
    <Transition name="slide-up">
      <div
        v-if="uploadSuccess"
        class="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-50"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
        <span class="font-medium">Profile photo updated successfully!</span>
      </div>
    </Transition>

    <!-- Error Notification -->
    <Transition name="slide-up">
      <div
        v-if="uploadError"
        class="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-50"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        <span class="font-medium">{{ uploadError }}</span>
      </div>
    </Transition>

    <!-- Modals -->
    <EditBioModal
      :is-open="isBioModalOpen"
      :current-bio="userBio"
      :is-saving="isSaving"
      @close="isBioModalOpen = false"
      @save="handleBioSave"
    />

    <EditSubjectsModal
      :is-open="isSubjectsModalOpen"
      :current-subjects="userSubjects"
      :available-subjects="availableSubjects"
      :is-saving="isSaving"
      @close="isSubjectsModalOpen = false"
      @save="handleSubjectsSave"
    />

    <UploadPhotoModal
      :is-open="isPhotoModalOpen"
      @close="isPhotoModalOpen = false"
      @upload="handlePhotoUploadFromModal"
    />

    <EditLearningStylesModal
      :is-open="isLearningStyleModalOpen"
      :current-styles="userLearningStyles"
      :available-styles="availableLearningStyles"
      :is-saving="isSaving"
      @close="isLearningStyleModalOpen = false"
      @save="handleLearningStylesSave"
    />

    <EditTextFieldModal
      :is-open="isPhoneModalOpen"
      title="Edit Phone Number"
      label="Phone Number"
      :current-value="userPhone"
      placeholder="+639XXXXXXXXX or 09XXXXXXXXX"
      :validate-fn="validatePhone"
      :is-saving="isSaving"
      @close="isPhoneModalOpen = false"
      @save="handlePhoneSave"
    />

    <EditLocationModal
      :is-open="isLocationModalOpen"
      :current-city="userCity"
      :is-saving="isSaving"
      @close="isLocationModalOpen = false"
      @save="handleLocationSave"
    />

    <EditTextFieldModal
      :is-open="isSchoolModalOpen"
      :current-value="userSchool"
      title="Edit School"
      label="School Name"
      placeholder="Enter your school name"
      :max-length="200"
      :is-saving="isSaving"
      @close="isSchoolModalOpen = false"
      @save="handleSchoolSave"
    />

    <EditGradeTrackModal
      :is-open="isGradeTrackModalOpen"
      :current-grade-level="userGradeLevel"
      :current-track="userAcademicTrack"
      :is-saving="isSaving"
      @close="isGradeTrackModalOpen = false"
      @save="handleGradeTrackSave"
    />

    <!-- ID Update Reminder Modal -->
    <IDUpdateReminderModal
      :is-open="isIDReminderModalOpen"
      :previous-grade="previousGradeForReminder"
      :current-grade="currentGradeForReminder"
      :change-type="idUpdateChangeType"
      @close="handleIDReminderClose"
      @upload="handleIDReminderUpload"
      @dismiss="handleIDReminderDismiss"
    />

    <!-- Upload ID Modal -->
    <UploadIDModal
      :is-open="isIDUploadModalOpen"
      :is-uploading="isUploadingID"
      @close="isIDUploadModalOpen = false"
      @upload="handleIDUpload"
    />

    <!-- Custom Notification Popup -->
    <transition name="backdrop">
      <div
        v-if="showNotificationPopup"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
        @click="closeNotificationPopup"
      >
        <transition name="notification">
          <div
            v-if="showNotificationPopup"
            class="bg-white rounded-lg p-6 border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] max-w-sm mx-4"
            @click.stop
          >
            <!-- Success Icon -->
            <div v-if="notificationType === 'success'" class="success-icon mb-4">
              <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle class="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
                <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
              </svg>
            </div>

            <!-- Error Icon -->
            <div v-else class="error-icon mb-4">
              <svg class="cross" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle class="cross-circle" cx="26" cy="26" r="25" fill="none" />
                <path class="cross-line" fill="none" d="M16 16 36 36" />
                <path class="cross-line" fill="none" d="M16 36 36 16" />
              </svg>
            </div>

            <h3 class="text-lg font-medium text-gray-900 mb-2 text-center">
              {{ notificationType === "success" ? "Success!" : "Error" }}
            </h3>
            <p class="text-sm text-gray-600 mb-4 text-center">{{ notificationMessage }}</p>
            <button
              @click="closeNotificationPopup"
              class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] active:shadow-none active:translate-x-1 active:translate-y-1"
            >
              OK
            </button>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<style scoped>
/* CRITICAL FIX: Override global overflow-x from main.css that breaks sticky */
.student-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/profile.png") center top/100% auto no-repeat;
  background-attachment: scroll;
  position: relative;
  width: 100%;
  overflow: visible !important; /* MUST override global overflow-x: hidden */
  overflow-x: clip; /* Modern alternative to prevent horizontal scroll without breaking sticky */
}

/* Sticky Sidebar - CRITICAL: Parent must not have overflow hidden/auto/scroll */
@media (min-width: 1024px) {
  .sticky-sidebar {
    position: -webkit-sticky; /* Safari compatibility */
    position: sticky;
    top: 10rem; /* Space for fixed header */
    align-self: flex-start;
    z-index: 10;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100px);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100px);
  opacity: 0;
}

/* Notification Popup Animations */
.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

.notification-enter-active {
  animation: notification-popup 0.3s ease-out;
}

.notification-leave-active {
  animation: notification-popup 0.2s ease-in reverse;
}

@keyframes notification-popup {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Success Icon Animation */
.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  animation: successPulse 0.5s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #10b981;
  stroke-miterlimit: 10;
}

.checkmark-circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke-miterlimit: 10;
  stroke: #10b981;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

/* Error Icon Animation */
.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  animation: successPulse 0.5s ease-out;
}

.cross {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #ef4444;
  stroke-miterlimit: 10;
}

.cross-circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke-miterlimit: 10;
  stroke: #ef4444;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.cross-line {
  stroke-dasharray: 30;
  stroke-dashoffset: 30;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards;
}
</style>
